import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Input,
  IconButton,
  Button,
  Box,
  HStack,
  InputRightElement,
  Text,
  InputGroup,
  VStack,
  Select,
} from '@chakra-ui/react';

import { ChevronUpIcon, ChevronDownIcon } from '@chakra-ui/icons';

import QuantityAdjustments from './QuantityAdjustments';
import SaveButton from './SaveButton';
import { useColors } from './colors';

const SourcingRulesTable = ({
  storeData,
  onStoreUpdate,
  hasChanges,
  onSave,
  isLoading,
}) => {
  const [suppliers, setSuppliers] = useState([]);
  const masterSuppliers = useSelector((state) => state.suppliers.list) || [];

  const { tableBgColor, borderColor, rowBorderColor, inputColor, placeholderColor } =
    useColors();

  const masterById = useMemo(
    () => new Map(masterSuppliers.map((s) => [String(s._id || s.id), s])),
    [masterSuppliers],
  );

  useEffect(() => {
    if (storeData?.suppliers) {
      // Map API data to component state format
      const mappedSuppliers = storeData.suppliers.map((supplier, index) => ({
        id: supplier.supplierId,
        altName: supplier.altName,
        markup: supplier.markupPercentage * 100 || 0, // Convert to percentage
        tolerance: supplier.priceTolerancePercentage * 100 || 0, // Convert to percentage
        stockFallback: supplier.stockAvailabilityFallback || false, // Use separate field
        priority: supplier.priorityRank || index + 1,
        inventorySubWarehouse: supplier.inventorySubWarehouse || '',
        originalData: supplier, // Keep reference to original data
      }));
      setSuppliers(mappedSuppliers);
    }
  }, [storeData]);

  const updateStoreData = (updatedSuppliers) => {
    // Convert back to API format
    const apiSuppliers = updatedSuppliers.map((supplier) => ({
      ...supplier.originalData,
      markupPercentage: supplier.markup / 100, // Convert back to decimal
      priceTolerancePercentage: supplier.tolerance / 100, // Convert back to decimal
      priorityRank: supplier.priority,
      stockAvailabilityFallback: supplier.stockFallback, // Use separate field
      inventorySubWarehouse: supplier.inventorySubWarehouse || null,
    }));

    const updatedStore = {
      ...storeData,
      suppliers: apiSuppliers,
    };

    onStoreUpdate(updatedStore);
  };

  const handleMarkupChange = (id, value) => {
    const updatedSuppliers = suppliers.map((s) =>
      s.id === id ? { ...s, markup: value } : s,
    );
    setSuppliers(updatedSuppliers);
    updateStoreData(updatedSuppliers);
  };

  const handleToleranceChange = (id, value) => {
    const updatedSuppliers = suppliers.map((s) =>
      s.id === id ? { ...s, tolerance: value } : s,
    );
    setSuppliers(updatedSuppliers);
    updateStoreData(updatedSuppliers);
  };

  const handleSubWarehouseChange = (id, value) => {
    const updatedSuppliers = suppliers.map((s) =>
      s.id === id ? { ...s, inventorySubWarehouse: value } : s,
    );
    setSuppliers(updatedSuppliers);
    updateStoreData(updatedSuppliers);
  };

  const toggleStockFallback = (id) => {
    const updatedSuppliers = suppliers.map((s) =>
      s.id === id ? { ...s, stockFallback: !s.stockFallback } : s,
    );
    setSuppliers(updatedSuppliers);
    updateStoreData(updatedSuppliers);
  };

  const movePriority = (id, direction) => {
    const index = suppliers.findIndex((s) => s.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === suppliers.length - 1)
    )
      return;

    const newSup = [...suppliers];
    const target = direction === 'up' ? index - 1 : index + 1;

    // swap priorities
    [newSup[index].priority, newSup[target].priority] = [
      newSup[target].priority,
      newSup[index].priority,
    ];
    // swap positions
    [newSup[index], newSup[target]] = [newSup[target], newSup[index]];

    setSuppliers(newSup);
    updateStoreData(newSup);
  };

  return (
    <VStack spacing={0} align="stretch">
      <Box
        bg={tableBgColor}
        borderRadius="xl"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead>
            <Tr borderBottom="1px" borderColor={rowBorderColor}>
              <Th>Supplier</Th>
              <Th>Mark Up (%)</Th>
              <Th>Price Offset Tolerance (%)</Th>
              <Th>Stock Availability Fallback</Th>
              <Th>Inventory region</Th>
              <Th>Priority</Th>
              <Th></Th>
            </Tr>
          </Thead>

          <Tbody>
            {suppliers.map((s) => {
              const master = masterById.get(String(s.id));
              const usesSubWarehouse = !!master?.usesSubWarehouse;
              const codes = Array.isArray(master?.subWarehouseCodes)
                ? master.subWarehouseCodes
                : [];
              return (
              <Tr key={s.id} borderBottom="1px" borderColor={rowBorderColor}>
                <Td fontWeight="medium">{s.altName}</Td>

                <Td>
                  <InputGroup size="sm" width="120px">
                    <Input
                      type="number"
                      value={s.markup || ''}
                      placeholder="Enter"
                      onChange={(e) => handleMarkupChange(s.id, Number(e.target.value))}
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                      pr="2.5rem"
                      color={inputColor}
                      _placeholder={{ color: placeholderColor }}
                    />
                    <InputRightElement pointerEvents="none" width="2.5rem">
                      <Text color="gray.500">%</Text>
                    </InputRightElement>
                  </InputGroup>
                </Td>

                <Td>
                  <InputGroup size="sm" width="120px">
                    <Input
                      type="number"
                      value={s.tolerance || ''}
                      placeholder="Enter"
                      onChange={(e) =>
                        handleToleranceChange(s.id, Number(e.target.value))
                      }
                      borderColor="gray.300"
                      _hover={{ borderColor: 'gray.400' }}
                      _focus={{ borderColor: 'blue.500', boxShadow: 'outline' }}
                      pr="2.5rem"
                      color={inputColor}
                      _placeholder={{ color: placeholderColor }}
                    />
                    <InputRightElement pointerEvents="none" width="2.5rem">
                      <Text color="gray.500">%</Text>
                    </InputRightElement>
                  </InputGroup>
                </Td>

                <Td>
                  <Button
                    size="sm"
                    colorScheme={s.stockFallback ? 'green' : 'gray'}
                    onClick={() => toggleStockFallback(s.id)}
                    width="100px"
                  >
                    {s.stockFallback ? 'Active' : 'Inactive'}
                  </Button>
                </Td>

                <Td>
                  {usesSubWarehouse && codes.length > 0 ? (
                    <Select
                      size="sm"
                      width="100px"
                      value={s.inventorySubWarehouse || ''}
                      placeholder="Select"
                      onChange={(e) => handleSubWarehouseChange(s.id, e.target.value)}
                      color={inputColor}
                    >
                      {codes.map((code) => (
                        <option key={code} value={code}>
                          {code}
                        </option>
                      ))}
                    </Select>
                  ) : (
                    <Text fontSize="sm" color="gray.500">
                      —
                    </Text>
                  )}
                </Td>

                <Td>
                  <Text>{s.priority}</Text>
                </Td>

                <Td>
                  <HStack spacing={1}>
                    <IconButton
                      aria-label="Move up"
                      icon={<ChevronUpIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => movePriority(s.id, 'up')}
                      isDisabled={suppliers.findIndex((su) => su.id === s.id) === 0}
                    />
                    <IconButton
                      aria-label="Move down"
                      icon={<ChevronDownIcon />}
                      size="sm"
                      variant="ghost"
                      onClick={() => movePriority(s.id, 'down')}
                      isDisabled={
                        suppliers.findIndex((su) => su.id === s.id) ===
                        suppliers.length - 1
                      }
                    />
                  </HStack>
                </Td>
              </Tr>
            );
            })}
          </Tbody>
        </Table>
      </Box>

      <QuantityAdjustments storeData={storeData} onStoreUpdate={onStoreUpdate} />

      <SaveButton hasChanges={hasChanges} onSave={onSave} isLoading={isLoading} />
    </VStack>
  );
};

export default SourcingRulesTable;
